import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useAuth, Role } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { allPartiesIncludingNonParliamentary, moroccoRegions } from "@/data/parties";
import { usePrioritiesStore, validatePriorityText, computeAgeGroup } from "@/data/prioritiesStore";
import { LogIn, MailCheck, CheckCircle2, MessageSquare, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { requestAccreditation } from "@/data/accreditationsStore";
import { useI18n } from "@/i18n/I18nProvider";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: "login" | "register";
  initialRegistrationType?: "normal" | "dedie";
  initialRole?: Role;
  lockSelection?: boolean;
}

const AuthModal = ({ isOpen, onClose, initialTab = "login", initialRegistrationType = "normal", initialRole = "CITIZEN", lockSelection = false }: AuthModalProps) => {
  const { login, register, forgotPassword } = useAuth();
  const { toast } = useToast();
  const { t, lang, isRtl } = useI18n();
  const { submitPriority } = usePrioritiesStore();
  
  const [activeTab, setActiveTab] = useState<"login" | "register">(initialTab);
  
  const previousPathRef = useRef("");

  useEffect(() => {
    if (isOpen) {
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        previousPathRef.current = window.location.pathname + window.location.search;
      }
      const targetPath = activeTab === 'register' ? '/register' : '/login';
      window.history.replaceState(null, '', targetPath);
    } else if (previousPathRef.current) {
      if (window.location.pathname === '/login' || window.location.pathname === '/register') {
        window.history.replaceState(null, '', previousPathRef.current);
      }
    }
  }, [isOpen, activeTab]);

  
  // Login State
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  
  // Forgot Password State
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  
  // Register State
  const [registrationType, setRegistrationType] = useState<"normal" | "dedie">(initialRegistrationType);
  const [role, setRole] = useState<Role>(initialRole);
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [regPhone, setRegPhone] = useState("");
  
  // Party / ONG specific
  const [partyType, setPartyType] = useState<"party" | "independent">("party");
  const [regParty, setRegParty] = useState("");
  const [qualite, setQualite] = useState("");
  const [ongName, setOngName] = useState("");
  
  // Institution specific
  const [institutionName, setInstitutionName] = useState("");
  const [qualiteInstitution, setQualiteInstitution] = useState("");
  
  // Media specific
  const [mediaType, setMediaType] = useState("tv");
  const [channelName, setChannelName] = useState("");

  // Citizen specific
  const [categorieSociopro, setCategorieSociopro] = useState("");
  const [genre, setGenre] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [regionResidence, setRegionResidence] = useState("");
  // Priorities (citoyen + ONG)
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [ageError, setAgeError] = useState("");

  // Priorities (citoyen + ONG)
  const [priority1, setPriority1] = useState("");
  const [priority2, setPriority2] = useState("");

  // Secondary Request State
  const [requiresSecondaryRequest, setRequiresSecondaryRequest] = useState(false);
  const [secondaryRequestMessage, setSecondaryRequestMessage] = useState("");
  const [secondaryRequestReason, setSecondaryRequestReason] = useState("");

  // Registration step
  const [isEmailSent, setIsEmailSent] = useState(false);

  const resetState = () => {
    setIsEmailSent(false);
    setLoginEmail("");
    setLoginPassword("");
    setRegistrationType(initialRegistrationType);
    setRole(initialRole);
    setActiveTab(initialTab);
    setRegName("");
    setRegEmail("");
    setRegPassword("");
    setRegPhone("");
    setPartyType("party");
    setRegParty("");
    setQualite("");
    setOngName("");
    setInstitutionName("");
    setQualiteInstitution("");
    setMediaType("tv");
    setChannelName("");
    setCategorieSociopro("");
    setGenre("");
    setAgeRange("");
    setRegionResidence("");
    setAcceptTerms(false);
    setAgeError("");
    setPriority1("");
    setPriority2("");
    setRequiresSecondaryRequest(false);
    setSecondaryRequestMessage("");
    setSecondaryRequestReason("");
  }

  const handleClose = () => {
    resetState();
    onClose();
  }

  // Update states when modal opens with new props
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setRegistrationType(initialRegistrationType);
      setRole(initialRole);
    }
  }, [isOpen, initialTab, initialRegistrationType, initialRole]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(loginEmail, loginPassword);
      handleClose();
    } catch (err) {
      toast({ title: "Erreur", description: "Identifiants incorrects ou compte inexistant.", variant: "destructive" });
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    
    try {
      await forgotPassword(forgotEmail);
      toast({
        title: "Email envoyé",
        description: "Si cette adresse existe, un lien de réinitialisation vous a été envoyé par e-mail.",
        variant: "default"
      });
      setShowForgotPassword(false);
      setForgotEmail("");
    } catch (err: any) {
      toast({
        title: "Erreur",
        description: err.message || "Impossible d'envoyer le lien.",
        variant: "destructive"
      });
    }
  };

  const processRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    setAgeError("");

    if (!regPassword || regPassword.length < 6) {
      toast({ title: "Erreur", description: "Le mot de passe doit contenir au moins 6 caractères.", variant: "destructive" });
      return;
    }

    if (registrationType === 'dedie') {
      const hasUppercase = /[A-Z]/.test(regPassword);
      const hasNumber = /[0-9]/.test(regPassword);
      const hasSymbol = /[^A-Za-z0-9]/.test(regPassword);
      
      if (!hasUppercase || !hasNumber || !hasSymbol) {
        toast({ 
          title: "Mot de passe faible", 
          description: "Pour les espaces dédiés, le mot de passe doit contenir au moins une majuscule, un chiffre et un symbole (ex: @,$,&,%).", 
          variant: "destructive" 
        });
        return;
      }
    }

    if (role === 'PARTY' && partyType === 'party' && !regParty) {
      toast({ title: "Erreur", description: "Veuillez sélectionner un parti politique.", variant: "destructive" });
      return;
    }

    if (registrationType === 'normal') {
      if (!ageRange) {
        setAgeError("Veuillez sélectionner une tranche d'âge.");
        return;
      }
      if (ageRange === "under18") {
        setAgeError("Vous devez avoir 18 ans ou plus pour vous inscrire en tant que citoyen.");
        return;
      }
      if (!regionResidence) {
        toast({ title: "Erreur", description: "Veuillez sélectionner votre région de résidence.", variant: "destructive" });
        return;
      }
    }

    if (!regName || !regEmail || !acceptTerms) return;
    
    try {
      // Call API to create user
      await executeRegistration();
      setIsEmailSent(true);
    } catch (e) {
      // Error is already handled by toast in executeRegistration
    }
  };

  const executeRegistration = async () => {
    try {
      await register({
        name: regName,
        email: regEmail,
        password: regPassword,
        role: registrationType === "normal" ? "CITIZEN" : role,
        phone: regPhone,
        partyAcronym: role === 'PARTY' && registrationType === 'dedie' ? (partyType === "independent" ? "SAP" : regParty) : undefined,
        isPartyDelegate: false, // Doit être validé manuellement plus tard (dashboard)
        requestedDelegateStatus: registrationType === 'dedie', // Tout espace dédié demande une accréditation
        ongName: role === 'ONG' && registrationType === 'dedie' ? ongName : undefined,
        institutionName: role === 'INSTITUTION' && registrationType === 'dedie' ? institutionName : undefined,
        qualite: role === 'INSTITUTION' && registrationType === 'dedie' ? qualiteInstitution : (((role === 'PARTY' || role === 'ONG' || role === 'MEDIA') && registrationType === 'dedie') ? qualite : undefined),
        channelName: role === 'MEDIA' ? channelName : undefined,
        mediaType: role === 'MEDIA' ? mediaType : undefined,
        genre: registrationType === 'normal' ? genre : undefined,
        age: registrationType === 'normal' ? ageRange : undefined,
        residence: registrationType === 'normal' ? regionResidence : undefined,
        categorieSociopro: registrationType === 'normal' ? categorieSociopro : undefined,
        secondaryRequestReason: requiresSecondaryRequest ? secondaryRequestReason : undefined
      });

      // Submit priorities to store
      if ((registrationType === 'normal' || role === 'ONG') && priority1.trim()) {
        submitPriority({
          userId: regEmail,
          userName: regName,
          role: registrationType === 'normal' ? 'CITIZEN' : 'ONG',
          genre: registrationType === 'normal' ? genre : undefined,
          ageGroup: registrationType === 'normal' ? ageRange : undefined,
          ongName: role === 'ONG' ? ongName : undefined,
          priority1: priority1.trim(),
          priority2: priority2.trim(),
        });
      }
      
      if (registrationType === 'dedie') {
        let requestedAcronym = role;
        if (role === 'PARTY') requestedAcronym = partyType === "independent" ? "SAP" : regParty;
        if (role === 'ONG') requestedAcronym = ongName || "ONG";
        if (role === 'INSTITUTION') requestedAcronym = institutionName || "INSTITUTION";
        if (role === 'MEDIA') requestedAcronym = channelName || "MEDIA";
        
        let requestedQualite = qualite;
        if (role === 'INSTITUTION') requestedQualite = qualiteInstitution;
        if (role === 'MEDIA') requestedQualite = mediaType;
        
        requestAccreditation(
          regEmail, 
          regName, 
          regEmail, 
          requestedAcronym, 
          "", // Empty doc for now
          {
            role: role,
            phone: regPhone,
            qualite: requestedQualite
          }
        );
      }
      
      handleClose();
    } catch (e: any) {
      if (e.message === "PRINCIPAL_EXISTS") {
        setRequiresSecondaryRequest(true);
        setSecondaryRequestMessage(lang === 'ar' 
          ? "تم منح الوصول إلى هذا الحساب بالفعل لمندوب رئيسي. إذا كنت جزءًا من الفريق، يرجى ملء طلب الوصول الإضافي أدناه."
          : "L'accès à ce compte est déjà attribué à un délégué principal. Si vous faites partie de l'équipe, veuillez remplir la demande d'accès supplémentaire ci-dessous.");
        throw e; // Re-throw to prevent showing success screen without closing modal
      }
      toast({ title: "Erreur", description: e.message || "Erreur lors de l'inscription.", variant: "destructive" });
      throw e; // Re-throw to prevent showing success screen
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px] rounded-3xl w-[95vw] p-0 overflow-hidden border-none shadow-2xl">
        <div className="h-2 w-full bg-gradient-to-r from-primary to-accent" />
        
        {!isEmailSent ? (
          <>
            <div className="px-6 pt-6 pb-2" dir={isRtl ? 'rtl' : 'ltr'}>
              <DialogTitle className="text-2xl font-display text-primary flex items-center gap-2">
                <LogIn className="w-6 h-6" /> {lang === 'ar' ? "مرحباً بكم في كاينين" : "Bienvenue sur Kayn'in"}
              </DialogTitle>
              <DialogDescription className="mt-2 text-muted-foreground font-body">
                {lang === 'ar' ? "انضم إلى المنصة المغربية للديمقراطية التشاركية." : "Rejoins l'espace citoyen nouvelle génération !"}
              </DialogDescription>
            </div>

            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full px-6 pb-6" dir={isRtl ? 'rtl' : 'ltr'}>
              <TabsList className="grid w-full grid-cols-2 mb-6 h-12 bg-muted/50 rounded-xl p-1">
                <TabsTrigger value="login" className="rounded-lg font-semibold">{lang === 'ar' ? "تسجيل الدخول" : "Connexion"}</TabsTrigger>
                <TabsTrigger value="register" className="rounded-lg font-semibold">{lang === 'ar' ? "إنشاء حساب" : "Inscription"}</TabsTrigger>
              </TabsList>
              
              {/* ONGLET CONNEXION */}
              <TabsContent value="login" className="mt-0">
                {showForgotPassword ? (
                  <form onSubmit={handleForgotPassword} className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                    <div className="text-center mb-2">
                      <h3 className="font-bold text-lg">{lang === 'ar' ? "نسيت كلمة المرور؟" : "Mot de passe oublié ?"}</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {lang === 'ar' ? "أدخل بريدك الإلكتروني. سنرسل لك رابطاً لإعادة تعيين كلمة المرور." : "Saisissez votre adresse e-mail. Nous vous enverrons un lien pour réinitialiser votre mot de passe."}
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-foreground px-1">Adresse Email</label>
                      <input 
                        type="email" 
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="w-full h-12 rounded-xl border border-input shadow-sm bg-background px-4 focus:ring-2 focus:ring-primary/20 transition-all outline-none" 
                        placeholder="contact@exemple.com" 
                        required 
                      />
                    </div>
                    <div className="flex flex-col gap-2 pt-2">
                      <Button type="submit" className="w-full h-12 rounded-xl font-bold bg-primary">
                        {lang === 'ar' ? "إرسال الرابط" : "Envoyer le lien"}
                      </Button>
                      <Button type="button" variant="ghost" className="w-full h-12 rounded-xl" onClick={() => setShowForgotPassword(false)}>
                        {lang === 'ar' ? "العودة لتسجيل الدخول" : "Retour à la connexion"}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleLogin} className="space-y-4 animate-in fade-in slide-in-from-left-4 duration-300">
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-foreground px-1">{lang === 'ar' ? "البريد الإلكتروني" : "Adresse Email"}</label>
                      <input 
                        type="email" 
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className={`w-full h-12 rounded-xl border border-input shadow-sm bg-background px-4 focus:ring-2 focus:ring-primary/20 transition-all outline-none ${isRtl ? 'text-right' : 'text-left'}`}
                        placeholder="contact@exemple.com" 
                        required 
                      />
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center px-1">
                        <label className="text-sm font-semibold text-foreground">{lang === 'ar' ? "كلمة المرور" : "Mot de passe"}</label>
                        <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs text-primary hover:underline font-medium">
                          {lang === 'ar' ? "نسيت كلمة المرور؟" : "Mot de passe oublié ?"}
                        </button>
                      </div>
                      <div className="relative">
                        <input 
                          type={showLoginPassword ? "text" : "password"} 
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          className={`w-full h-12 rounded-xl border border-input shadow-sm bg-background ${isRtl ? 'pr-4 pl-10' : 'pl-4 pr-10'} focus:ring-2 focus:ring-primary/20 transition-all outline-none ${isRtl ? 'text-right' : 'text-left'}`}
                          placeholder="••••••••"
                          required 
                        />
                        <button 
                          type="button" 
                          onPointerDown={() => setShowLoginPassword(true)}
                          onPointerUp={() => setShowLoginPassword(false)}
                          onPointerLeave={() => setShowLoginPassword(false)}
                          className={`absolute top-1/2 -translate-y-1/2 ${isRtl ? 'left-3' : 'right-3'} text-muted-foreground hover:text-foreground touch-none select-none`}
                        >
                          {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full h-12 rounded-xl font-bold bg-primary mt-2">
                      {lang === 'ar' ? "تسجيل الدخول" : "Se connecter"}
                    </Button>
                  </form>
                )}
              </TabsContent>

              {/* ONGLET INSCRIPTION */}
              <TabsContent value="register" className="mt-0">
                <form onSubmit={processRegistration} className="space-y-4 px-1 custom-scrollbar pb-2">
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-foreground px-1">{lang === 'ar' ? "نوع الفضاء *" : "Type d'espace d'inscription *"}</label>
                      <Select disabled={lockSelection} value={registrationType} onValueChange={(val) => { setRegistrationType(val as "normal" | "dedie"); if (val === "normal") setRole("CITIZEN"); }}>
                        <SelectTrigger className={`w-full h-12 rounded-xl shadow-sm border-input ${isRtl ? 'flex-row-reverse' : ''}`}>
                          <SelectValue placeholder="..." />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                          <SelectItem value="normal" className={isRtl ? 'text-right' : 'text-left'}>🧑‍🤝‍🧑 {lang === 'ar' ? "ولوج عادي (العموم)" : "Accès normal (Grand public)"}</SelectItem>
                          <SelectItem value="dedie" className={isRtl ? 'text-right' : 'text-left'}>💼 {lang === 'ar' ? "فضاء مخصص (يخضع لشروط)" : "Espace Dédié (Accès soumis à conditions)"}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {registrationType === 'dedie' && (
                      <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1">
                        <label className="text-sm font-semibold text-primary px-1">{lang === 'ar' ? "فئة الفضاء المخصص *" : "Catégorie de l'espace dédié *"}</label>
                        <Select disabled={lockSelection} value={role === 'CITIZEN' ? '' : role} onValueChange={(val) => setRole(val as Role)}>
                          <SelectTrigger className={`w-full h-12 rounded-xl shadow-sm border-primary/40 bg-primary/5 ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <SelectValue placeholder="..." />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl">
                            <SelectItem value="PARTY" className={isRtl ? 'text-right' : 'text-left'}>🏛️ {lang === 'ar' ? "سياسي (حزب أو مستقل)" : "Politicien (Parti ou Indépendant)"}</SelectItem>
                            <SelectItem value="ONG" className={isRtl ? 'text-right' : 'text-left'}>🤝 {lang === 'ar' ? "جمعية / منظمة" : "ONG / Association"}</SelectItem>
                            <SelectItem value="INSTITUTION" className={isRtl ? 'text-right' : 'text-left'}>🏛️ {lang === 'ar' ? "مؤسسة" : "Institution"}</SelectItem>
                            <SelectItem value="MEDIA" className={isRtl ? 'text-right' : 'text-left'}>📺 {lang === 'ar' ? "إعلام / برنامج" : "Média / Émission"}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* CHAMPS COMMUNS */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-foreground px-1">
                      {registrationType === 'dedie' && role === 'MEDIA' 
                        ? (lang === 'ar' ? 'الاسم الكامل لمراسل الإعلام *' : 'Nom complet du correspondant média *') 
                        : (lang === 'ar' ? 'الاسم الكامل *' : 'Nom complet *')}
                    </label>
                    <input type="text" value={regName} onChange={(e) => setRegName(e.target.value)} className={`w-full h-12 rounded-xl border border-input shadow-sm bg-background px-4 focus:ring-2 focus:ring-primary/20 transition-all outline-none ${isRtl ? 'text-right' : 'text-left'}`} required />
                  </div>
                  
                  <div className={`grid ${registrationType === 'dedie' ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-foreground px-1">{lang === 'ar' ? "البريد الإلكتروني *" : "Email *"}</label>
                      <input type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} className={`w-full h-12 rounded-xl border border-input shadow-sm bg-background px-4 focus:ring-2 focus:ring-primary/20 transition-all outline-none ${isRtl ? 'text-right' : 'text-left'}`} required />
                    </div>
                    {registrationType === 'dedie' && (
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-foreground px-1">{lang === 'ar' ? "الهاتف *" : "Téléphone *"}</label>
                        <input type="tel" value={regPhone} pattern="^[+]?[0-9]{10,}$" title="num incorrect" onChange={(e) => setRegPhone(e.target.value)} className={`w-full h-12 rounded-xl border border-input shadow-sm bg-background px-4 focus:ring-2 focus:ring-primary/20 transition-all outline-none ${isRtl ? 'text-right' : 'text-left'}`} required />
                      </div>
                    )}
                  </div>

                  {/* SPÉCIFIQUE INSTITUTION */}
                  {registrationType === 'dedie' && role === 'INSTITUTION' && (
                    <div className="space-y-3 animate-in fade-in zoom-in-95 bg-teal-50 dark:bg-teal-950/20 p-4 rounded-xl border border-teal-200 dark:border-teal-800">
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-teal-700 dark:text-teal-300 px-1">Nom de l'institution *</label>
                        <input
                          type="text"
                          value={institutionName}
                          onChange={(e) => setInstitutionName(e.target.value)}
                          className="w-full h-12 rounded-xl shadow-sm border border-teal-200 dark:border-teal-700 bg-background px-4 focus:ring-2 focus:ring-teal-400/30 outline-none"
                          placeholder="Ministère, Commune, Université..."
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-teal-700 dark:text-teal-300 px-1">Qualité au sein de l'institution *</label>
                        <input
                          type="text"
                          value={qualiteInstitution}
                          onChange={(e) => setQualiteInstitution(e.target.value)}
                          className="w-full h-12 rounded-xl shadow-sm border border-teal-200 dark:border-teal-700 bg-background px-4 focus:ring-2 focus:ring-teal-400/30 outline-none"
                          placeholder="Directeur, Chef de service, Président..."
                          required
                        />
                      </div>
                    </div>
                  )}

                  {/* SPÉCIFIQUE PARTI POLITIQUE & ONG */}
                  {registrationType === 'dedie' && (role === 'PARTY' || role === 'ONG') && (
                    <>
                      {role === 'PARTY' ? (
                        <div className="space-y-4 animate-in fade-in zoom-in-95 bg-primary/5 p-4 rounded-xl border border-primary/20">
                          <div className="space-y-1.5">
                            <label className="text-sm font-semibold text-primary px-1">Type (appartenant/indépendant) *</label>
                            <Select value={partyType} onValueChange={(v) => { setPartyType(v as "party" | "independent"); if (v === "independent") setRegParty("SAP"); }}>
                              <SelectTrigger className="w-full h-12 rounded-xl shadow-sm border-primary/30 bg-background">
                                <SelectValue placeholder="Sélectionnez..." />
                              </SelectTrigger>
                              <SelectContent className="rounded-xl">
                                <SelectItem value="party">Appartenant (Parti Politique)</SelectItem>
                                <SelectItem value="independent">Indépendant (SAP)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {partyType === "party" && (
                            <div className="space-y-1.5 mt-2">
                              <label className="text-sm font-semibold text-primary px-1">Parti Politique représenté *</label>
                              <Select value={regParty} onValueChange={setRegParty} required>
                                <SelectTrigger className="w-full h-12 rounded-xl shadow-sm border-primary/30 bg-background">
                                  <SelectValue placeholder="Sélectionnez un parti..." />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl">
                                  {allPartiesIncludingNonParliamentary.filter(p => p.acronym !== "SAP").map((p) => {
                                    const partyName = t.parties?.partiesNames ? t.parties.partiesNames[p.acronym] : undefined;
                                    const arabicName = (p as any).acronymAr;
                                    const display = partyName || (lang === 'ar' && arabicName ? `${arabicName} (${p.acronym})` : p.acronym);
                                    return <SelectItem key={p.acronym} value={p.acronym}>{display}</SelectItem>;
                                  })}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-1.5 animate-in fade-in zoom-in-95 bg-primary/5 p-4 rounded-xl border border-primary/20">
                          <div className="space-y-1.5 mb-3">
                            <label className="text-sm font-semibold text-primary px-1">Nom de l'ONG / Association *</label>
                            <input type="text" value={ongName} onChange={(e) => setOngName(e.target.value)} className="w-full h-12 rounded-xl shadow-sm border border-primary/30 bg-background px-4 focus:ring-2 focus:ring-primary/20 outline-none" placeholder="Association Marocaine pour..." required />
                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-1.5 animate-in fade-in transition-all mt-4">
                        <label className="text-sm font-semibold text-foreground px-1">Qualité au sein {role === 'PARTY' ? (partyType === 'independent' ? 'de la liste locale' : 'du parti') : 'du bureau'} *</label>
                        <input type="text" value={qualite} onChange={(e) => setQualite(e.target.value)} className="w-full h-12 rounded-xl border border-input shadow-sm bg-background px-4 focus:ring-2 focus:ring-primary/20 transition-all outline-none" placeholder={role === 'PARTY' && partyType === 'independent' ? "Tête de liste, Coordinateur..." : "Secrétaire Général, Président..."} required />
                      </div>
                    </>
                  )}

                  {/* SPÉCIFIQUE MÉDIA */}
                  {role === 'MEDIA' && (
                    <div className="space-y-3 animate-in fade-in zoom-in-95 bg-purple-50 dark:bg-purple-950/20 p-4 rounded-xl border border-purple-200 dark:border-purple-800">
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-purple-700 dark:text-purple-300 px-1">Support Média *</label>
                        <Select value={mediaType} onValueChange={setMediaType} required>
                          <SelectTrigger className="w-full h-12 rounded-xl shadow-sm border-purple-200 dark:border-purple-700 bg-background">
                            <SelectValue placeholder="Sélectionnez..." />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl">
                            <SelectItem value="tv">Télévision (TV)</SelectItem>
                            <SelectItem value="radio">Radio</SelectItem>
                            <SelectItem value="web">Web TV</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-purple-700 dark:text-purple-300 px-1">Nom de la Chaîne / Support *</label>
                        <input type="text" value={channelName} onChange={(e) => setChannelName(e.target.value)} className="w-full h-12 rounded-xl shadow-sm border border-purple-200 dark:border-purple-700 bg-background px-4 focus:ring-2 focus:ring-purple-400/30 outline-none" placeholder="Ex: 2M, Al Aoula, Medi1..." required />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-purple-700 dark:text-purple-300 px-1">Qualité du profil inscrit *</label>
                        <input type="text" value={qualite} onChange={(e) => setQualite(e.target.value)} className="w-full h-12 rounded-xl shadow-sm border border-purple-200 dark:border-purple-700 bg-background px-4 focus:ring-2 focus:ring-purple-400/30 outline-none" placeholder="Producteur, Présentateur..." required />
                      </div>
                    </div>
                  )}

                  {/* SPÉCIFIQUE ESPACE NORMAL (CITOYEN) */}
                  {registrationType === 'normal' && (
                    <div className="space-y-4 animate-in fade-in zoom-in-95 bg-muted/30 p-4 rounded-xl border border-border">
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <label className="text-sm font-semibold text-foreground px-1">{lang === 'ar' ? 'الجنس *' : 'Genre *'}</label>
                          <Select value={genre} onValueChange={setGenre} required>
                            <SelectTrigger className={`w-full h-12 rounded-xl bg-background shadow-sm border-input ${isRtl ? 'flex-row-reverse' : ''}`}>
                              <SelectValue placeholder="..." />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                              <SelectItem value="homme">Homme</SelectItem>
                              <SelectItem value="femme">Femme</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5 w-full">
                          <label className="text-sm font-semibold text-foreground px-1">{lang === 'ar' ? 'الفئة العمرية *' : "Tranche d'âge *"}</label>
                          <Select value={ageRange} onValueChange={setAgeRange} required>
                            <SelectTrigger className={`w-full h-12 rounded-xl bg-background shadow-sm border-input ${isRtl ? 'flex-row-reverse' : ''}`}>
                              <SelectValue placeholder="..." />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                              <SelectItem value="under18">Moins de 18 ans</SelectItem>
                              <SelectItem value="18-25">18 - 25 ans</SelectItem>
                              <SelectItem value="26-35">26 - 35 ans</SelectItem>
                              <SelectItem value="36-45">36 - 45 ans</SelectItem>
                              <SelectItem value="46-59">46 - 59 ans</SelectItem>
                              <SelectItem value="60+">60 ans et plus</SelectItem>
                            </SelectContent>
                          </Select>
                          {ageError && <p className="text-[11px] text-red-500 font-semibold px-1 py-1 leading-tight">{ageError}</p>}
                        </div>
                      </div>

                      <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1">
                        <label className="text-sm font-semibold text-foreground px-1">{lang === 'ar' ? 'الجهة *' : lang === 'zgh' ? 'ⵜⴰⵎⵏⴰⴹⵜ *' : 'Région *'}</label>
                        <Select value={regionResidence} onValueChange={setRegionResidence} required>
                          <SelectTrigger className="w-full h-12 rounded-xl bg-background shadow-sm border-input">
                            <SelectValue placeholder="Sélectionnez..." />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl max-h-[60vh] overflow-y-auto">
                            {moroccoRegions.map(r => (
                              <SelectItem key={r.name} value={r.name}>{lang === 'ar' ? r.nameAr : lang === 'zgh' ? r.nameZgh : r.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1 mt-4">
                        <label className="text-sm font-semibold text-foreground px-1">{lang === 'ar' ? 'النشاط (اختياري)' : 'Activité (optionnel)'}</label>
                        <input type="text" value={categorieSociopro} onChange={(e) => setCategorieSociopro(e.target.value)} className={`w-full h-12 rounded-xl border border-input shadow-sm bg-background px-4 focus:ring-2 focus:ring-primary/20 transition-all outline-none ${isRtl ? 'text-right' : 'text-left'}`} placeholder={lang === 'ar' ? 'طالب، حرفي، جمعوي...' : 'Mécanicien, Associatif, Étudiant...'} />
                      </div>
                    </div>
                  )}

                  {/* PRIORITÉS CITOYENNES — pour CITIZEN, ONG et INSTITUTION (optionnel) */}
                  {(role === 'CITIZEN' || role === 'ONG' || role === 'INSTITUTION') && (
                    <div className="space-y-3 animate-in fade-in zoom-in-95 bg-pink-50 dark:bg-pink-950/20 p-4 rounded-xl border border-pink-200 dark:border-pink-800">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="w-4 h-4 text-pink-600" />
                        <p className="text-sm font-bold text-pink-800 dark:text-pink-300">Vos 2 priorités citoyennes</p>
                        <span className="ml-auto text-[10px] text-pink-500 font-semibold bg-pink-100 dark:bg-pink-900/40 px-2 py-0.5 rounded-full">Optionnel • Max 3 mots</span>
                      </div>
                      <p className="text-[11px] text-pink-600 dark:text-pink-400 font-body -mt-1">
                        Ces priorités seront publiées sur la plateforme après validation, contribuant à l'analyse des attentes citoyennes.
                      </p>
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-foreground px-1">Ma 1ère priorité <span className="text-muted-foreground font-normal">(optionnel)</span></label>
                        <input
                          type="text"
                          value={priority1}
                          onChange={e => { const v = e.target.value; if (v.split(/\s+/).filter(Boolean).length <= 3) setPriority1(v); }}
                          className="w-full h-10 rounded-xl border border-pink-200 dark:border-pink-800 bg-background px-4 text-sm focus:ring-2 focus:ring-pink-400/30 outline-none"
                          placeholder="Ex : Emploi jeunes"
                        />
                        <p className="text-[10px] text-muted-foreground px-1">{priority1.split(/\s+/).filter(Boolean).length}/3 mots</p>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-foreground px-1">Ma 2ème priorité <span className="text-muted-foreground font-normal">(optionnel)</span></label>
                        <input
                          type="text"
                          value={priority2}
                          onChange={e => { const v = e.target.value; if (v.split(/\s+/).filter(Boolean).length <= 3) setPriority2(v); }}
                          className="w-full h-10 rounded-xl border border-pink-200 dark:border-pink-800 bg-background px-4 text-sm focus:ring-2 focus:ring-pink-400/30 outline-none"
                          placeholder="Ex : Santé accessible"
                        />
                        <p className="text-[10px] text-muted-foreground px-1">{priority2.split(/\s+/).filter(Boolean).length}/3 mots</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5 pt-2">
                    <label className="text-sm font-semibold text-foreground px-1">{lang === 'ar' ? 'كلمة المرور *' : 'Mot de passe *'}</label>
                    <div className="relative">
                      <input 
                        type={showRegPassword ? "text" : "password"} 
                        value={regPassword} 
                        onChange={(e) => setRegPassword(e.target.value)} 
                        className={`w-full h-12 rounded-xl border border-input shadow-sm bg-background ${isRtl ? 'pr-4 pl-10' : 'pl-4 pr-10'} focus:ring-2 focus:ring-primary/20 transition-all outline-none ${isRtl ? 'text-right' : 'text-left'}`} 
                        required 
                      />
                      <button 
                        type="button" 
                        onPointerDown={() => setShowRegPassword(true)}
                        onPointerUp={() => setShowRegPassword(false)}
                        onPointerLeave={() => setShowRegPassword(false)}
                        className={`absolute top-1/2 -translate-y-1/2 ${isRtl ? 'left-3' : 'right-3'} text-muted-foreground hover:text-foreground touch-none select-none`}
                      >
                        {showRegPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {requiresSecondaryRequest && (
                    <div className="space-y-3 p-4 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/30 animate-in fade-in zoom-in-95 mt-4">
                      <div className="flex gap-2 text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                        <p className="text-sm font-semibold leading-tight">{secondaryRequestMessage}</p>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-amber-900 dark:text-amber-100 px-1">{lang === 'ar' ? 'سبب الطلب *' : 'Motif de la demande *'}</label>
                        <textarea 
                          value={secondaryRequestReason} 
                          onChange={(e) => setSecondaryRequestReason(e.target.value)} 
                          className="w-full h-24 rounded-xl border border-amber-300 shadow-sm bg-white dark:bg-background p-3 focus:ring-2 focus:ring-amber-400/50 transition-all outline-none resize-none text-sm" 
                          placeholder={lang === 'ar' ? "مثال: أنا مسؤول عن التواصل، يرجى منحي إمكانية الوصول..." : "Ex: Je suis chargé de la communication, veuillez me donner l'accès..."}
                          required
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col gap-3 mt-4 p-4 rounded-xl border border-border bg-muted/20">
                    <div className="flex items-start gap-3 w-full">
                      <input 
                        type="checkbox" 
                        id="terms" 
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-input text-primary focus:ring-primary/50 shrink-0 cursor-pointer"
                        required
                      />
                      <label htmlFor="terms" className="text-sm text-foreground/80 leading-tight cursor-pointer font-body">
                        {lang === 'ar' 
                          ? <span>أصرح بشرفي صحة المعلومات المقدمة وأوافق على <span className="text-primary hover:underline font-semibold">شروط الاستخدام العامة</span> وكذلك احترام <span className="text-primary hover:underline font-semibold cursor-pointer">ميثاق الأخلاقيات</span>.</span>
                          : <span>Je déclare sur l'honneur l'exactitude des informations fournies et j'accepte les <span className="text-primary hover:underline font-semibold">Conditions Générales d'Utilisation</span> ainsi que le respect de la <span className="text-primary hover:underline font-semibold cursor-pointer">Charte d'Éthique</span>.</span>
                        }
                      </label>
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-12 rounded-xl font-bold bg-primary mt-6">
                    {requiresSecondaryRequest 
                      ? (lang === 'ar' ? 'إرسال طلب الوصول' : 'Envoyer la demande d\'accès')
                      : (lang === 'ar' ? 'متابعة' : 'Continuer')
                    }
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="p-8 text-center animate-in zoom-in-95 duration-500">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-display font-bold text-foreground mb-3">Inscription réussie !</h3>
            <p className="text-muted-foreground font-body mb-8">
              Votre compte a été créé avec succès. 
              {role === "PARTY" || role === "ONG" || role === "INSTITUTION" || role === "MEDIA" 
                ? " En tant qu'espace dédié, vous pouvez maintenant accéder à votre tableau de bord. Notez que certaines fonctionnalités nécessiteront une validation de l'administrateur." 
                : " Vous pouvez maintenant participer et donner votre avis sur les différents modules de Kayn'in."}
            </p>
            
            <div className="space-y-3">
              <Button onClick={handleClose} className="w-full h-12 rounded-xl font-bold bg-primary text-white">
                Accéder à la plateforme
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
